import ConditionOperators from './condition-operators';

export default class CriteriaCondition {
    op = null;

    condition = null;

    conditions = null;

    field = null;

    path = null;

    value = null;

    values = null;

    listId = null;

    min = null;

    max = null;

    comparePath = null;

    constructor({
        op = null,
        condition = null,
        conditions = null,
        field = null,
        path = null,
        value = null,
        values = null,
        listId = null,
        min = null,
        max = null,
        comparePath = null,
    } = {}) {
        this.op = op;
        this.condition = condition
            ? new CriteriaCondition(condition)
            : null;
        this.conditions = conditions
            ? conditions.map(v => new CriteriaCondition(v))
            : null;
        this.field = field;
        this.path = path;
        this.value = value;
        this.values = values;
        this.listId = listId;
        this.min = min;
        this.max = max;
        this.comparePath = comparePath;
    }

    /**
     * Determine if this condition level is a negation.
     * @returns {boolean}
     */
    isNegative() {
        return this.op === ConditionOperators.not;
    }

    /**
     * Determine whether the condition is a union.
     * @returns {boolean}
     */
    isUnion() {
        return this.op === ConditionOperators.and;
    }

    /**
     * Determine whether the condition is a disjunction (or).
     * @return {boolean}
     */
    isDisjunction() {
        return this.op === ConditionOperators.or;
    }

    /**
     * Determine whether the condition is composed
     * of one or more conditions underneath itself.
     * @returns {boolean}
     */
    isComposed() {
        return this.op === ConditionOperators.not
            || this.op === ConditionOperators.or
            || this.op === ConditionOperators.and;
    }

    /**
     * Determine whether the condition defines an unknown value.
     * @returns {boolean}
     */
    isUnknown() {
        return this.op === ConditionOperators.equals && this.value === null;
    }

    /**
     * Determine whether the condition defines a negative unknown
     * value.
     * @returns {boolean}
     */
    isNotUnknown() {
        return this.isParentNegative && this.isUnknown();
    }

    /**
     * Determine whether the condition is comparing two paths.
     * @returns {boolean}
     */
    isComparison() {
        return this.op === ConditionOperators.same;
    }

    /**
     * Determine whether the condition is referencing a value list.
     * @returns {boolean}
     */
    isValueList() {
        return this.op === ConditionOperators.inList;
    }

    /**
     * Determine whether the condition contains other conditions regardless
     * of the operator.
     * @returns {boolean}
     */
    hasConditions() {
        return this.conditions !== null && Array.isArray(this.conditions);
    }

    /**
     * Determine whether the condition is `contains`.
     * @returns {boolean}
     */
    isContains() {
        return this.op === ConditionOperators.contains;
    }

    /**
     * Check whether the condition has no operator and therefore
     * is newly added to the UI.
     * @returns {boolean}
     */
    isEmpty() {
        return this.op === null;
    }

    /**
     * Check whether the condition is the special random operator
     * which doesn't require a path to function
     * @return {boolean}
     */
    isRandom() {
        return this.op === ConditionOperators.random;
    }

    /**
     * Check whether the condition is a range.
     * @return {boolean}
     */
    isRange() {
        return this.op === ConditionOperators.between;
    }

    /**
     * Check whether the condition is a simple list of values.
     * @return {boolean}
     */
    isSimpleList() {
        return this.op === ConditionOperators.in;
    }

    /**
     * Placeholder method used to determine if the condition does not follow
     * the convention of API fields.
     * @return {boolean}
     */
    isSpecialCondition() {
        return [this.isRandom()].some(Boolean);
    }

    /**
     * Finds a condition contained within which matches the
     * predicate provided.
     * @param {Function} predicate
     * @returns {CriteriaCondition|null}
     */
    findComposed(predicate) {
        const match = this.conditions.find(child => predicate(child)
            || (child.isNegative() && predicate(child.condition)));
        return match || null;
    }

    /**
     * Recursively generate a payload from this condition level.
     * @returns {{op}}
     */
    getPayload() {
        const summarize = (condition) => {
            const composeOperation = data => ({
                op: condition.op,
                ...data,
            });
            const pathBased = data => ({
                ...composeOperation(data),
                path: condition.path,
            });
            if (condition.op === null) {
                return null;
            }
            if (condition.isContains()) {
                return pathBased({
                    condition: summarize(condition.condition),
                });
            }
            if (condition.isComposed()) {
                if (condition.isNegative()) {
                    return composeOperation({condition: summarize(condition.condition)});
                }
                return composeOperation({
                    // filter out `null` conditions from the array
                    conditions: condition.conditions.map(summarize)
                        .filter(v => v !== null),
                });
            }
            if ([
                ConditionOperators.equals,
                ConditionOperators.lte,
                ConditionOperators.gte,
            ].includes(condition.op)) {
                return pathBased({value: condition.value});
            }
            if (condition.op === ConditionOperators.inList) {
                return pathBased({listId: condition.listId});
            }
            if (condition.op === ConditionOperators.in) {
                return pathBased({values: condition.values});
            }
            if (condition.op === ConditionOperators.same) {
                return pathBased({comparePath: condition.comparePath});
            }
            if (condition.op === ConditionOperators.between) {
                return pathBased({
                    min: condition.min,
                    max: condition.max,
                });
            }
            if (condition.isRandom()) {
                return composeOperation({value: condition.value});
            }

            return composeOperation({});
        };
        return summarize(this);
    }

    getPath() {
        if (this.isComposed()) {
            if (this.isNegative()) {
                return this.condition.getPath();
            }
            // only an `or` could exist at this level
            // in order to have relevant path information
            const [condition] = this.conditions;
            return condition.getPath();
        }
        if (this.isContains()) {
            return `${this.path}${this.condition.getPath() || ''}`;
        }
        return this.path;
    }
}
